/* ==========================================================================
   Cart Drawer v2 — CartAPI + <cart-drawer> Web Component (updated 2026-03-31)
   ========================================================================== */

// ---------------------------------------------------------------------------
// Part A: Cart API Helper
// ---------------------------------------------------------------------------

const SECTIONS_TO_RENDER = 'v2-cart-drawer,v2-cart-icon-bubble,v2-cart-recommendations';

// Retry helper for Shopify 429 rate-limit responses
async function fetchWithRetry(url, options, maxRetries = 3) {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const response = await fetch(url, options);
    if (response.status === 429 && attempt < maxRetries) {
      // Exponential backoff: 2s, 4s, 8s
      await new Promise(r => setTimeout(r, 2000 * Math.pow(2, attempt)));
      continue;
    }
    return response;
  }
}

const CartAPI = {
  async add(formData) {
    if (formData instanceof FormData) {
      formData.append('sections', SECTIONS_TO_RENDER);
    }
    const response = await fetchWithRetry(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      body: formData
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const err = new Error(error.description || `Add failed: ${response.status}`);
      err.shopifyError = error;
      throw err;
    }
    return response.json();
  },

  async change(key, quantity, sellingPlan) {
    const body = { id: String(key), quantity, sections: SECTIONS_TO_RENDER };
    if (sellingPlan !== undefined) body.selling_plan = sellingPlan;
    const response = await fetchWithRetry(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(`Change failed: ${response.status}`);
    return response.json();
  },

  async update(updates) {
    const response = await fetchWithRetry(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...updates, sections: SECTIONS_TO_RENDER })
    });
    if (!response.ok) throw new Error(`Update failed: ${response.status}`);
    return response.json();
  },

  async clear() {
    const response = await fetchWithRetry(`${window.Shopify.routes.root}cart/clear.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sections: SECTIONS_TO_RENDER })
    });
    if (!response.ok) throw new Error(`Clear failed: ${response.status}`);
    return response.json();
  },

  async get() {
    const response = await fetchWithRetry(`${window.Shopify.routes.root}cart.js`, {});
    if (!response.ok) throw new Error(`Get failed: ${response.status}`);
    return response.json();
  }
};

window.CartAPI = CartAPI;

// ---------------------------------------------------------------------------
// Part B: <cart-drawer> Web Component
// ---------------------------------------------------------------------------

class CartDrawerElement extends HTMLElement {
  connectedCallback() {
    // Cache DOM references
    this.overlay = this.querySelector('.cart-drawer-overlay');
    this.panel = this.querySelector('.cart-drawer');
    this.body = this.querySelector('.cart-drawer__body');
    this.closeBtn = this.querySelector('[data-action="close"]');
    this.countEl = this.querySelector('.cart-drawer__count');
    this.statusEl = this.querySelector('[data-cart-status]');

    // Instance state
    this.debounceTimers = {};
    this.triggerElement = null;
    this._queue = [];
    this._processing = false;

    // Bind handlers (store references for removal)
    this._handleClick = this.handleClick.bind(this);
    this._handleChange = this.handleChange.bind(this);
    this._onKeydown = this.onKeydown.bind(this);
    this._onFormSubmit = this.onFormSubmit.bind(this);
    this._onCartTrigger = this.onCartTrigger.bind(this);

    // Cart interaction listeners — document-level so they work on both
    // the cart drawer AND the /cart page (which is outside <cart-drawer>)
    document.addEventListener('click', this._handleClick);
    document.addEventListener('change', this._handleChange);

    // Document listeners
    document.addEventListener('keydown', this._onKeydown);
    document.addEventListener('submit', this._onFormSubmit, true); // capture phase — fires before old ProductForm handler
    document.addEventListener('click', this._onCartTrigger, true); // capture phase

    // Populate recommendations slot from SSR source
    const recsSource = this.querySelector('.cart-drawer__recs-source');
    const recsSlot = this.body?.querySelector('.cart-drawer__recs-slot');
    if (recsSource && recsSlot) {
      const wrapper = recsSource.querySelector('[id^="shopify-section-"]');
      recsSlot.innerHTML = wrapper ? wrapper.innerHTML : recsSource.innerHTML;
    }

    // Observe footer height changes (Rise.ai widget can load asynchronously)
    this.footer = this.body?.querySelector('.cart-drawer__footer');
    if (this.footer) {
      this._footerObserver = new ResizeObserver(() => this.updateBodyPadding());
      this._footerObserver.observe(this.footer);
      this.updateBodyPadding();
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('submit', this._onFormSubmit, true);
    document.removeEventListener('click', this._onCartTrigger, true);
    document.removeEventListener('click', this._handleClick);
    document.removeEventListener('change', this._handleChange);
    if (this._footerObserver) this._footerObserver.disconnect();
  }

  // -----------------------------------------------------------------------
  // Action Queue — processes mutations sequentially, never drops actions
  // -----------------------------------------------------------------------

  async enqueue(fn) {
    this._queue.push(fn);
    if (this._processing) return;
    this._processing = true;
    while (this._queue.length > 0) {
      const task = this._queue.shift();
      try {
        await task();
      } catch (err) {
        console.error('Cart queue task failed:', err);
      }
    }
    this._processing = false;
  }

  // -----------------------------------------------------------------------
  // Event Handlers
  // -----------------------------------------------------------------------

  handleClick(e) {
    // Only process clicks from inside the cart drawer or cart page
    if (!e.target.closest('cart-drawer') && !e.target.closest('.cart-page')) return;

    // Check overlay click
    if (e.target === this.overlay) {
      this.close();
      return;
    }

    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;
    const key = actionEl.dataset.key;

    switch (action) {
      case 'close':
        this.close();
        break;

      case 'decrease': {
        const currentQty = parseInt(actionEl.dataset.qty, 10) || 0;
        const newQty = Math.max(0, currentQty - 1);
        if (key) {
          this.setCheckoutLoading(true);
          this.debouncedChange(key, newQty);
        }
        break;
      }

      case 'increase': {
        const currentQty = parseInt(actionEl.dataset.qty, 10) || 0;
        const newQty = currentQty + 1;
        if (key) {
          this.setCheckoutLoading(true);
          this.debouncedChange(key, newQty);
        }
        break;
      }

      case 'remove':
        if (key) {
          const itemEl = actionEl.closest('.cart-item');
          this.removeItem(key, itemEl);
        }
        break;

      case 'subscribe': {
        const { variantId, sellingPlanId, quantity } = actionEl.dataset;
        if (key && variantId && sellingPlanId) {
          this.handleSubscribe(key, variantId, sellingPlanId, parseInt(quantity, 10) || 1);
        }
        break;
      }

      case 'remove-bundle':
        this.removeBundle();
        break;

      case 'add-rec':
        this.addRecItem(actionEl);
        break;
    }
  }

  handleChange(e) {
    // Only process changes from inside the cart drawer or cart page
    if (!e.target.closest('cart-drawer') && !e.target.closest('.cart-page')) return;

    const input = e.target;

    // Frequency selector change
    if (input.dataset.action === 'change-frequency') {
      const { key, variantId, quantity } = input.dataset;
      const newPlanId = input.value;
      if (key && variantId && newPlanId) {
        this.handleFrequencyChange(key, variantId, newPlanId, parseInt(quantity, 10) || 1);
      }
      return;
    }

    // Quantity input change
    if (!input.hasAttribute('data-quantity-input')) return;

    const key = input.dataset.key;
    const qty = Math.max(0, parseInt(input.value, 10) || 0);
    if (key) {
      this.debouncedChange(key, qty);
    }
  }

  onKeydown(e) {
    if (e.key === 'Escape' && this.classList.contains('is-open')) {
      this.close();
    }
  }

  onFormSubmit(e) {
    const form = e.target;
    if (!form || !form.action || !form.action.includes('/cart/add')) return;

    // Don't intercept bundle builder product forms — they handle their own selection
    if (form.closest('bundle-builder')) return;

    e.preventDefault();
    e.stopImmediatePropagation(); // block old ProductForm handler in legacy-global.js
    const submitBtn = e.submitter || form.querySelector('[type="submit"]');
    this.addToCart(form, submitBtn);
  }

  onCartTrigger(e) {
    const trigger = e.target.closest('[data-ajax-cart-trigger]');
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();
    this.open();
  }

  // -----------------------------------------------------------------------
  // Core Methods
  // -----------------------------------------------------------------------

  updateBodyPadding() {
    if (!this.footer || !this.body) return;
    const footerH = this.footer.offsetHeight;
    if (footerH > 0) {
      this.body.style.paddingBottom = footerH + 'px';
    }
  }

  open() {
    this.triggerElement = document.activeElement;
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      if (this.closeBtn) this.closeBtn.focus();
    });
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
      this.triggerElement.focus();
    }
  }

  setCheckoutLoading(loading) {
    const btn = this.querySelector('.cart-drawer__checkout');
    if (btn) btn.classList.toggle('is-loading', loading);
  }

  async addToCart(form, submitBtn) {
    if (submitBtn && submitBtn.disabled) return;

    // Disable button to prevent double-submit
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.classList.add('is-loading');
    }

    const reenable = () => {
      // Re-query from live DOM — the original submitBtn reference may be stale
      // if a V8 product swap replaced the form content while we were waiting.
      const btn = submitBtn?.isConnected
        ? submitBtn
        : form.querySelector('[type="submit"]');
      if (btn) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.classList.remove('is-loading');
      }
    };

    this.enqueue(async () => {
      try {
        const formData = new FormData(form);
        // The form may contain multiple input[name="id"] (ATC + Shop Pay messaging).
        // After a V8 formula swap only the first (inside #product-atc) is updated.
        // FormData.get() returns the first value per spec; delete all and re-append it.
        const correctId = formData.get('id');
        formData.delete('id');
        formData.append('id', correctId);
        const data = await CartAPI.add(formData);

        // Extract product title for announcements
        const titleEl = form.closest('[data-product-title]')
          || document.querySelector('h1.product-title, h1.product_name, .product__title h1');
        const productTitle = titleEl ? titleEl.textContent.trim() : '';

        this.renderSections(data.sections, 'add', productTitle);

        // PDP "methods" / Bundle & Save section: fire a distinct event with the
        // method's own title (the generic add_to_cart above uses the main PDP
        // title and can't identify which method was added).
        if (form.closest('.v8-bundle-section')) {
          const methodCard = form.closest('.v8-bundle-product') || form.closest('card-product');
          const methodTitleEl = methodCard ? methodCard.querySelector('h4') : null;
          const methodTitle = methodTitleEl ? methodTitleEl.textContent.trim() : productTitle;
          this.pushDataLayerEvent('hs_pdp_method_add', methodTitle, correctId);
        }

        this.open();

        // Re-enable button after brief delay so user sees the drawer open first
        setTimeout(reenable, 500);
      } catch (err) {
        console.error('Add to cart error:', err);
        // Surface error message on the product form (e.g., sold out)
        const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
        const errorMsg = form.querySelector('.product-form__error-message');
        if (errorWrapper && errorMsg) {
          errorMsg.textContent = err.message || 'Could not add item to cart';
          errorWrapper.removeAttribute('hidden');
        }
        reenable();
      }
    });
  }

  debouncedChange(key, qty) {
    clearTimeout(this.debounceTimers[key]);
    this.debounceTimers[key] = setTimeout(() => {
      this.changeQuantity(key, qty);
    }, 500);
  }

  async changeQuantity(key, qty) {
    const action = qty === 0 ? 'remove' : 'update';
    const itemEl = this.querySelector(`.cart-item[data-key="${key}"]`);
    const nameEl = itemEl ? itemEl.querySelector('.cart-item__name') : null;
    const productTitle = nameEl ? nameEl.textContent.trim() : '';
    this.setCheckoutLoading(true);
    this.enqueue(async () => {
      try {
        const data = await CartAPI.change(key, qty);
        this.renderSections(data.sections, action, productTitle);
      } catch (err) {
        console.error('Change quantity error:', err);
        this.setCheckoutLoading(false);
      }
    });
  }

  removeItem(key, itemEl) {
    const nameEl = itemEl ? itemEl.querySelector('.cart-item__name') : null;
    const productTitle = nameEl ? nameEl.textContent.trim() : '';
    this.setCheckoutLoading(true);
    this.enqueue(async () => {
      try {
        const data = await CartAPI.change(key, 0);
        this.renderSections(data.sections, 'remove', productTitle);
      } catch (err) {
        console.error('Remove item error:', err);
        this.setCheckoutLoading(false);
      }
    });
  }

  async handleSubscribe(key, variantId, sellingPlanId, quantity) {
    this.setCheckoutLoading(true);
    // Capture the line item title before the cart re-renders.
    const itemEl = this.querySelector(`.cart-item[data-key="${key}"]`);
    const nameEl = itemEl ? itemEl.querySelector('.cart-item__name') : null;
    const productTitle = nameEl ? nameEl.textContent.trim() : '';
    this.enqueue(async () => {
      try {
        const data = await CartAPI.change(key, quantity, parseInt(sellingPlanId, 10));
        this.renderSections(data.sections, 'update', '');
        this.pushDataLayerEvent('hs_subscription_upgrade', productTitle, variantId);
      } catch (err) {
        console.error('Subscribe error:', err);
        this.setCheckoutLoading(false);
      }
    });
  }

  async handleFrequencyChange(key, variantId, newPlanId, quantity) {
    this.setCheckoutLoading(true);
    this.enqueue(async () => {
      try {
        const data = await CartAPI.change(key, quantity, parseInt(newPlanId, 10));
        this.renderSections(data.sections, 'update', '');
      } catch (err) {
        console.error('Frequency change error:', err);
        this.setCheckoutLoading(false);
      }
    });
  }

  async removeBundle() {
    this.setCheckoutLoading(true);

    this.enqueue(async () => {
      try {
        // Collect all bundle item keys and set quantity to 0
        const bundleItems = this.querySelectorAll('.cart-bundle__item[data-key]');
        const updates = {};
        bundleItems.forEach(item => {
          updates[item.dataset.key] = 0;
        });

        const data = await CartAPI.update({ updates });
        this.renderSections(data.sections, 'remove', 'Custom Bundle');

        // Notify bundle builder to re-enable (if on bundle builder page)
        const bundleBuilder = document.querySelector('bundle-builder');
        if (bundleBuilder && typeof bundleBuilder.enableBundleBuilder === 'function') {
          bundleBuilder.enableBundleBuilder();
        }
      } catch (err) {
        console.error('Remove bundle error:', err);
        this.setCheckoutLoading(false);
      }
    });
  }

  renderSections(sections, action, productTitle) {
    if (!sections) return;

    // 1. Update drawer content
    if (sections['v2-cart-drawer']) {
      const html = this.getSectionHTML(sections['v2-cart-drawer']);
      if (this.body) this.body.innerHTML = html;

      // Read item count from section wrapper
      const inner = this.body.querySelector('#cart-drawer-inner');
      if (inner && this.countEl) {
        const count = inner.dataset.cartItemCount || '0';
        this.countEl.textContent = count;
        this.countEl.setAttribute('aria-label', `${count} items in cart`);
      }

      // Re-cache the status element (it was inside the replaced HTML)
      this.statusEl = this.querySelector('[data-cart-status]');

      // Re-observe footer for height changes (Rise.ai widget injects asynchronously)
      this.footer = this.body?.querySelector('.cart-drawer__footer');
      if (this.footer && this._footerObserver) {
        this._footerObserver.disconnect();
        this._footerObserver.observe(this.footer);
      }
      this.updateBodyPadding();

      // Sync cart page body if on /cart (same section, separate DOM location)
      const cartPageBody = document.querySelector('.cart-page__body');
      if (cartPageBody) {
        cartPageBody.innerHTML = sections['v2-cart-drawer'];
        const cartPageCount = document.querySelector('.cart-page__count');
        if (cartPageCount) {
          const count = inner?.dataset?.cartItemCount || '0';
          cartPageCount.textContent = count;
        }
      }
    }

    // 2. Inject recommendations section into slot
    if (sections['v2-cart-recommendations']) {
      const recsHTML = this.getSectionHTML(sections['v2-cart-recommendations']);
      const recsSlot = this.body?.querySelector('.cart-drawer__recs-slot');
      if (recsSlot) recsSlot.innerHTML = recsHTML;
    }

    // 3. Update all header cart badges
    if (sections['v2-cart-icon-bubble']) {
      const temp = document.createElement('div');
      temp.innerHTML = sections['v2-cart-icon-bubble'];
      const countSource = temp.querySelector('[data-cart-count]');
      const newCount = countSource ? countSource.textContent.trim() : '0';
      const countNum = parseInt(newCount, 10) || 0;

      // Update all [data-cart-count] elements across the page
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = newCount;
      });

      // Legacy compat
      document.querySelectorAll('[data-bind="itemCount"]').forEach(el => {
        el.textContent = newCount;
      });

      // Toggle has-cart-count class on triggers
      document.querySelectorAll('[data-ajax-cart-trigger]').forEach(el => {
        el.classList.toggle('has-cart-count', countNum > 0);
      });
    }

    // 3. Re-trigger currency converter
    this.triggerCurrencyConverter();

    // 4. Screen reader announcement
    this.announce(action, productTitle);

    // 5. Analytics
    this.pushAnalytics(action, productTitle);

  }

  getSectionHTML(sectionHTML) {
    const temp = document.createElement('div');
    temp.innerHTML = sectionHTML;
    const wrapper = temp.querySelector('[id^="shopify-section-"]');
    return wrapper ? wrapper.innerHTML : sectionHTML;
  }

  triggerCurrencyConverter() {
    if (window.PXUTheme && window.PXUTheme.currencyConverter
        && typeof window.PXUTheme.currencyConverter.convertCurrencies === 'function') {
      window.PXUTheme.currencyConverter.convertCurrencies();
    }
  }

  announce(action, productTitle) {
    if (!this.statusEl) return;

    let message = '';
    switch (action) {
      case 'add':
        message = productTitle ? `${productTitle} added to cart` : 'Item added to cart';
        break;
      case 'remove':
        message = productTitle ? `${productTitle} removed from cart` : 'Item removed from cart';
        break;
      case 'update':
        message = productTitle ? `Quantity updated for ${productTitle}` : 'Cart updated';
        break;
    }

    this.statusEl.textContent = message;
  }

  pushAnalytics(action, productTitle) {
    if (!window.dataLayer) return;

    if (action === 'add') {
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: { items: [{ item_name: productTitle }] }
      });
    } else if (action === 'remove') {
      window.dataLayer.push({
        event: 'remove_from_cart',
        ecommerce: { items: [{ item_name: productTitle }] }
      });
    }
  }

  // Push a custom GA4 interaction event to GTM's dataLayer. Used for the
  // contextual cart/ATC events (subscription upgrade, cart recommendation add,
  // PDP method add) on top of the standard add_to_cart/remove_from_cart events.
  pushDataLayerEvent(eventName, productTitle, variantId) {
    if (!window.dataLayer) return;
    const item = {};
    if (productTitle) item.item_name = productTitle;
    if (variantId) item.item_variant = String(variantId);
    window.dataLayer.push({
      event: eventName,
      ecommerce: { items: [item] }
    });
  }


  async addRecItem(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    // Pull the recommendation's product title from its card for analytics.
    const card = btn.closest('.cart-rec-card');
    const titleEl = card ? card.querySelector('.cart-rec-card__title') : null;
    const productTitle = titleEl ? titleEl.textContent.trim() : '';

    btn.disabled = true;
    btn.classList.add('is-loading');
    this.setCheckoutLoading(true);

    this.enqueue(async () => {
      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');

        const data = await CartAPI.add(formData);
        this.renderSections(data.sections, 'add', productTitle);
        this.pushDataLayerEvent('hs_cart_recommendation_add', productTitle, variantId);
        this.open();
      } catch (err) {
        console.error('Add rec item error:', err);
        btn.disabled = false;
        btn.classList.remove('is-loading');
        this.setCheckoutLoading(false);
      }
    });
  }
}

customElements.define('cart-drawer', CartDrawerElement);
